import { BadgeCheck, CheckCircle2 } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";

const TEAM = [
  {
    initials: "SS",
    name: "Surinder Singh",
    role: "Registered Migration Agent",
    badge: "MARN 2217752 · QEAC M729",
    featured: true,
    bio: [
      "Surinder Singh is a Registered Migration Agent with over 10 years of experience in the Australian education and migration industry. He provides professional migration advice and supports clients across a wide range of Australian visa and migration pathways.",
      "He holds a Master of Professional Accounting and a Graduate Diploma in Australian Migration Law and Practice. He is also a Qualified Education Agent Counsellor (QEAC ID: M729), giving him a strong understanding of both Australia's education and migration systems.",
    ],
    areasTitle: "Areas of Expertise",
    areas: [
      "Skilled Migration pathways",
      "Student and Graduate Visas",
      "Employer-Sponsored Visas",
      "Family and Partner Visas",
      "Visa eligibility assessments and migration strategy",
      "Migration application review and lodgement",
    ],
  },
  {
    initials: "AA",
    name: "Arun Acharya",
    role: "Director | Administrative & Communication Support to Registered Migration Agent | Online & Marketing Manager",
    bio: [
      "Arun leads the day-to-day operations of the business and oversees client communication, marketing, and administrative coordination. With over six years of experience in Australia's education and migration industry, he ensures clients receive timely support and a smooth experience throughout their journey.",
      "In his role, Arun provides administrative and communication support to the Registered Migration Agent, coordinating documentation, client follow-ups, appointment scheduling, and case progress while ensuring migration advice is provided by the Registered Migration Agent.",
    ],
    areasTitle: "Areas of Responsibility",
    areas: [
      "Business operations and administration",
      "Client communication and case coordination",
      "Administrative support to the Registered Migration Agent",
      "Digital marketing and online brand management",
      "Education counselling and admissions coordination",
      "Partnership and stakeholder relationship management",
    ],
  },
  {
    initials: "SA",
    name: "Sujata Aryal",
    role: "Account Analyst | Client Support",
    bio: [
      "Sujata Aryal has over 3 years of experience in the Australian education and migration industry, with a strong background in accounts administration, client support, and compliance coordination.",
      "She has worked under the direct supervision of a CPA, gaining practical experience in Australian accounting processes, taxation documentation, payroll, BAS preparation support, invoicing, and business administration.",
      "At Directive Pathway Advisors, Sujata coordinates all accounting and financial administration, ensuring client records, documentation, and financial processes are managed efficiently. She also works closely with our outsourced CPA, maintaining ongoing communication to support taxation, BAS, payroll, and other accounting services.",
    ],
    areasTitle: "Areas of Expertise",
    areas: [
      "Accounts administration and invoicing",
      "Client onboarding and document collection",
      "Tax return and accounting document coordination",
      "BAS, payroll, and compliance support coordination",
      "Administrative support for education and migration services",
      "Client communication and operational support",
    ],
  },
];

export default function Team() {
  return (
    <section id="team" className="bg-gradient-to-b from-sky-50 to-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Chapter number="04" label="Core Team" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-extrabold tracking-[-0.03em] text-slate-900 sm:text-4xl lg:text-5xl">
            Meet the People Behind <span className="text-sky-600">Your Pathway</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 max-w-xl text-base text-slate-600 sm:text-lg">
            A dedicated Melbourne team combining education counselling, client
            support, and registered migration expertise.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={0.08 * i} className="h-full">
              <article
                data-testid={`team-member-card-${i + 1}`}
                className={`flex h-full flex-col rounded-3xl border p-7 transition-all duration-500 hover:-translate-y-2 ${
                  m.featured
                    ? "border-sky-300 bg-white shadow-[0_24px_70px_rgba(2,132,199,0.18)] ring-1 ring-sky-200"
                    : "border-sky-100 bg-white/80 hover:border-sky-300 hover:shadow-[0_24px_60px_rgba(2,132,199,0.14)]"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-sky-600 font-display text-lg font-extrabold text-white shadow-[0_10px_24px_rgba(2,132,199,0.35)]">
                    {m.initials}
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-bold tracking-tight text-slate-900">
                      {m.name}
                    </h3>
                    <p className="mt-0.5 text-xs font-semibold leading-snug text-sky-700">{m.role}</p>
                  </div>
                </div>

                {m.badge && (
                  <p className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-sky-600 px-3.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-white">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    {m.badge}
                  </p>
                )}

                <div className="mt-5 space-y-3">
                  {m.bio.map((p, j) => (
                    <p key={j} className="text-sm leading-relaxed text-slate-600">
                      {p}
                    </p>
                  ))}
                </div>

                <div className="mt-6 flex-1 rounded-2xl bg-sky-50/80 p-5">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-sky-600">
                    {m.areasTitle}
                  </p>
                  <ul className="mt-3 space-y-2">
                    {m.areas.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-xs leading-relaxed text-slate-700">
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-500" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
